// Typed client for the owner-only admin API (/api/admin/*). Every data call is
// gated server-side (require_admin → 403) — this client just talks to it. Shares
// the credentialed `api` wrapper so the admin session cookie rides along.
import { api } from './api';

export interface AdminMe {
  admin: boolean;
  username?: string;
}

export type PlanLabel = 'free' | 'pro' | 'lifetime';

export interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  picture_url: string | null;
  created_at: string | null;
  last_login_at: string | null;
  plan: PlanLabel;
  status: string;
  product_id: string;
  subscription_id: string;
  plan_updated: string | null;
  sections_count: number;
  separations: number;
  sep_last: string | null;
  // How they sign in. Derived server-side from whether a password hash exists
  // and whether the account has a Google identity — the hash itself is never
  // sent. 'both' = one account reachable either way.
  auth_method?: 'google' | 'password' | 'both' | 'apple' | 'google+apple' | 'apple+password' | 'all';
  // False only for an email+password signup that hasn't followed its activation
  // link; Google accounts are verified at sign-in.
  activated?: boolean;
  // Acquisition, captured once at signup (first-touch). 'unknown' for every
  // account created before attribution shipped on 2026-08-28.
  signup_source?: string;   // 'chatgpt' | 'google' | 'chrome_store' | 'direct' | 'ref:<host>' | 'unknown'
  signup_landing?: string | null;  // first page they ever landed on
  signup_device?: string;   // 'mobile' | 'tablet' | 'desktop' | 'unknown'
  signup_surface?: string;  // 'web' | 'extension' | 'unknown'
  last_device?: string;     // refreshes on every sign-in
  // Which product they actually USE, from the separation log's surface — NOT
  // signup_surface, which only records where the account was created. 'none' =
  // never ran a separation; 'unknown' = only ran ones logged before the
  // extension started reporting a surface (every such row predates 2026-07-07).
  // 'android' = ran at least one in the Android app; it wins over the web buckets.
  uses?: 'android' | 'extension' | 'studio' | 'both' | 'unknown' | 'none';
  sep_ext?: number;         // separations run in the extension
  sep_studio?: number;      // separations run in the Studio
  sep_android?: number;     // separations run in the Android app — absent when there are none
}

export interface AdminFacet { key: string; n: number }

export interface AdminUsersResp {
  users: AdminUserRow[];
  total: number;
  page: number;
  per: number;
  q: string;
  source?: string;
  device?: string;
  surface?: string;
  uses?: string;
  // Counts across ALL users (not the active filter) so chips keep their totals.
  facets?: { source: AdminFacet[]; device: AdminFacet[]; surface: AdminFacet[]; uses?: AdminFacet[] };
}

export interface AdminNote {
  id: number | string;
  kind: 'note' | 'plan_change';
  body: string;
  created_at: string;
}

// One exit-survey submission from riffloop.app/uninstall. Identity fields are a
// snapshot taken at uninstall time, so they survive the account going away;
// `user_id` is null when the uninstall was anonymous (signed-out, or a build
// published before the extension started passing uid).
export interface AdminUninstallRow {
  id: number;
  user_id: string | null;
  email: string | null;
  name: string | null;
  picture_url: string | null;
  reason: string;
  note: string;
  cid: string | null;
  ext_version: string | null;
  created_at: string | null;
  reported_plan: string;      // what the extension reported at uninstall time
  plan: PlanLabel | null;     // live plan today; null when unidentified
  user_created_at: string | null;
  last_login_at: string | null;
  separations: number;
}

export interface AdminUninstallsResp {
  rows: AdminUninstallRow[];
  total: number;
  page: number;
  per: number;
  q: string;
  by_reason: { reason: string; c: number }[];
  stats: { all: number; with_notes: number; identified: number; last30: number };
}

export interface UninstallsQuery {
  q?: string;
  reason?: string;
  notes?: boolean;
  page?: number;
  per?: number;
}

// In-extension review prompt (star picker after the 2nd separation). Same
// snapshot-plus-live-join shape as the uninstall rows.
export interface AdminReviewRow {
  id: number;
  user_id: string | null;
  email: string | null;
  name: string | null;
  picture_url: string | null;
  stars: number;              // 1-5; 4-5 were routed on to the Chrome Web Store
  note: string;               // verbatim 1-3-star feedback ('' for star-only rows)
  source: string;             // 'extension' | 'studio' | 'other'
  ext_version: string | null;
  created_at: string | null;
  reported_plan: string;      // tier the client reported at rating time
  plan: PlanLabel | null;     // live plan today; null when unidentified
  user_created_at: string | null;
  last_login_at: string | null;
  separations: number;
}

export interface AdminReviewsResp {
  rows: AdminReviewRow[];
  total: number;
  page: number;
  per: number;
  q: string;
  by_stars: { stars: number; c: number }[];
  stats: { all: number; avg: number; promoters: number; with_notes: number; identified: number; last30: number };
}

export interface ReviewsQuery {
  q?: string;
  stars?: string;             // 'all' | '1'..'5'
  notes?: boolean;
  page?: number;
  per?: number;
}

export interface StemCount {
  stem: string;
  c: number;
}
export interface SourceCount {
  source_kind: string;
  c: number;
}
export interface SurfaceCount {
  surface: string;
  c: number;
}
export interface SepEvent {
  video_id: string;
  // The real track name, borrowed from a section the user saved for the same
  // video. Null when they never saved one (or on an older backend) — render the
  // URL/id instead.
  title?: string | null;
  source_kind: string;
  stem: string;
  surface: string | null;
  source_url: string | null;
  created_at: string;
}
export interface LatestSection {
  id: string;
  label: string;
  stem: string;
  video_id: string;
  // The source track's real name. Null on rows saved before the column existed.
  title?: string | null;
  source_kind: string;
  updated_at: string;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    name: string | null;
    picture_url: string | null;
    created_at: string | null;
    last_login_at: string | null;
    updated_at: string | null;
    signup_source?: string;
    signup_landing?: string | null;
    signup_referrer?: string | null;
    signup_device?: string;
    signup_surface?: string;
    last_device?: string;
  };
  subscription: {
    plan: PlanLabel;
    status: string;
    is_lifetime: number;
    product_id: string;
    subscription_id: string;
    updated_at: string | null;
    created_at: string | null;
    order_id: string | null;
    amount: string | null;
    currency: string | null;
    raw: Record<string, unknown> | null;
  };
  activity: {
    separations: {
      total: number;
      last7: number;
      last30: number;
      last: string | null;
      first: string | null;
      distinct_tracks: number;
      by_stem: StemCount[];
      by_source: SourceCount[];
      by_surface: SurfaceCount[];
      recent: SepEvent[];
    };
    sections: {
      count: number;
      distinct_tracks: number;
      latest: LatestSection[];
      by_stem: StemCount[];
    };
    cap_hits: number;
    // What this person DID, newest first — the trail the Features tab cannot show
    // because it counts across everybody. Optional: a dashboard build can be newer
    // than the API it is talking to, and the panel simply does not draw.
    events?: {
      count: number;
      first: string | null;
      by_surface: SurfaceCount[];
      recent: FeatureEvent[];
    };
  };
  notes: AdminNote[];
}

export interface FeatureEvent {
  event: string;
  surface: string | null;
  /** Stored as JSON text, or null. Shape varies by event — decoded where it is drawn. */
  meta: string | null;
  created_at: string;
}

export interface RecentSeparation {
  video_id: string;
  source_kind: string;
  stem: string;
  created_at: string;
  email: string | null;
  name: string | null;
}
export interface RecentSignup {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
}

export interface AdminOverview {
  users: { total: number; new7: number; new30: number };
  active: { dau: number; wau: number; mau: number; login7: number; login30: number };
  plans: { free: number; pro: number; monthly: number; annual: number; lifetime: number; paid: number };
  conversion: number;
  mrr: number;
  arr: number;
  lifetime_revenue: number;
  separations: { total: number; last7: number; daily: { d: string; c: number }[] };
  top_tracks: { video_id: string; source_kind: string; c: number; user_name?: string | null; user_email?: string | null; user_count?: number }[];
  recent: RecentSeparation[];
  recent_signups: RecentSignup[];
}

// Feature usage + retention. Everything here is read-only.
export interface AdminFeatureRow {
  event: string;
  d1: number;
  d7: number;
  d30: number;
  users30: number;
  total: number;
}

export interface AdminFeatures {
  ok: boolean;
  events: AdminFeatureRow[];
  walls: { length_30s: number; daily_limit: number };
  retention: {
    cohort: number;
    returned: number;
    // null when the cohort is empty — render "not enough data", never 0%.
    rate: number | null;
    window: string;
  };
  depth: { '1': number; '2_3': number; '4_10': number; '11_plus': number };
  reach: { users: number; ever_separated: number; never_separated: number };
  // ISO date of the first row in feature_events, or null before any arrive.
  // The UI needs this to say "collecting since X" instead of letting empty
  // counts read as "nobody uses this".
  events_since: string | null;
  // Last 30 days per value of one meta field, top 8, for the events the server
  // lists in RL_FEATURE_SPLITS (which preset, which goal, how a modal closed).
  // Absent on a server from before 2026-09-14.
  splits?: Record<string, { v: string; n: number }[]>;
}

// /admin → Splitter. Shape produced by studio-php/api/splitter_stats.php.
export interface SplitterStep { key: string; label: string; n: number; visitors: number }
export interface SplitterSession {
  vid: string;
  first_at: string;
  last_at: string;
  tier: string | null;
  device: string | null;
  source: string | null;
  ref: string | null;
  email: string | null;
  steps: string[];
  last_event: string;
  events: number;
  purchased: boolean;
}
export interface AdminSplitter {
  ok: boolean;
  days: number;
  since: string | null;
  visitors: number;
  steps: SplitterStep[];
  side: SplitterStep[];
  views: {
    by_tier: Record<string, number>;
    by_device: Record<string, number>;
    by_source: Record<string, number>;
    by_ref: Record<string, number>;
  };
  waits: {
    previews: number; avg_secs: number | null; median_secs: number | null;
    warm: number; cold: number; warm_avg: number | null; cold_avg: number | null;
  };
  errors: { stage: string; reason: string; n: number }[];
  cancels: { n: number; avg_pct: number };
  leaves: { phase: string; n: number; avg_pct: number; avg_secs: number }[];
  modal: {
    opens: number; visitors: number;
    by_from: Record<string, number>; closes_by_via: Record<string, number>;
    avg_open_secs: number | null; plan_clicks: Record<string, number>; toggles: number;
  };
  tools: Record<string, number>;
  mixes: Record<string, number>;
  studio: { handoffs: number; arrived: number };
  checkout: { logins: number; starts: number; errors: number };
  purchases: { email: string; plan: string; at: string | null; first_seen: string }[];
  daily: { d: string; views: number; uploads: number; previews: number; modal: number; plan_clicks: number }[];
  sessions: SplitterSession[];
}

export interface UsersQuery {
  q?: string;
  plan?: 'all' | 'free' | 'pro' | 'lifetime';
  source?: string;
  device?: string;
  surface?: string;
  uses?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  per?: number;
}

export type AdminPlanTarget = 'free' | 'monthly' | 'annual' | 'lifetime';

export type CheckoutMode = 'test' | 'live';

export interface AdminSettings {
  ok: boolean;
  // Effective PayPro checkout mode. 'test' = PayPro test orders (no real charge);
  // 'live' = real checkout that charges customers.
  checkout_mode: CheckoutMode;
  // Where the live value comes from: 'db' = an explicit admin override, 'config'
  // = falling back to the PAYPRO_TEST_GRANTS config flag.
  source: 'db' | 'config';
  config_default: CheckoutMode;
  // Test orders need the checkout secret key; live webhooks need the IPN
  // validation key. Surfaced so the admin sees what's configured before flipping.
  secret_key_set: boolean;
  webhook_validation_set: boolean;
  webhook_url: string;
  // Admin-editable DISPLAY prices (drive what the app shows, NOT what PayPro charges).
  prices: PriceSet;
  updated_at: string | null;
  updated_by: string | null;
}

export interface PriceSet {
  monthly: string;
  annual: string;
  lifetime: string;
}

export const adminApi = {
  me: () => api.get<AdminMe>('/api/admin/me'),
  login: (username: string, password: string) =>
    api.post<{ ok: boolean; sent?: boolean; method?: 'totp' | 'email'; step?: string; detail?: string }>('/api/admin/login', { username, password }),
  verify: (code: string) => api.post<{ ok: boolean; admin: boolean }>('/api/admin/verify', { code }),
  logout: () => api.post<{ ok: boolean }>('/api/admin/logout'),
  users: (q: UsersQuery = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set('q', q.q);
    if (q.plan && q.plan !== 'all') p.set('plan', q.plan);
    if (q.source && q.source !== 'all') p.set('source', q.source);
    if (q.device && q.device !== 'all') p.set('device', q.device);
    if (q.surface && q.surface !== 'all') p.set('surface', q.surface);
    if (q.uses && q.uses !== 'all') p.set('uses', q.uses);
    if (q.sort) p.set('sort', q.sort);
    if (q.dir) p.set('dir', q.dir);
    if (q.page) p.set('page', String(q.page));
    if (q.per) p.set('per', String(q.per));
    const qs = p.toString();
    return api.get<AdminUsersResp>('/api/admin/users' + (qs ? '?' + qs : ''));
  },
  user: (id: string) => api.get<AdminUserDetail>('/api/admin/users/' + encodeURIComponent(id)),
  setPlan: (id: string, plan: AdminPlanTarget, note?: string) =>
    api.post<{ ok: boolean; plan: string }>('/api/admin/users/' + encodeURIComponent(id) + '/plan', { plan, note }),
  addNote: (id: string, body: string) =>
    api.post<{ ok: boolean; note: AdminNote }>('/api/admin/users/' + encodeURIComponent(id) + '/note', { body }),
  overview: () => api.get<AdminOverview>('/api/admin/overview'),
  features: () => api.get<AdminFeatures>('/api/admin/features'),
  splitter: (days: number) => api.get<AdminSplitter>('/api/admin/splitter?days=' + days),
  uninstalls: (q: UninstallsQuery = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set('q', q.q);
    if (q.reason && q.reason !== 'all') p.set('reason', q.reason);
    if (q.notes) p.set('notes', '1');
    if (q.page) p.set('page', String(q.page));
    if (q.per) p.set('per', String(q.per));
    const qs = p.toString();
    return api.get<AdminUninstallsResp>('/api/admin/uninstalls' + (qs ? '?' + qs : ''));
  },
  reviews: (q: ReviewsQuery = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set('q', q.q);
    if (q.stars && q.stars !== 'all') p.set('stars', q.stars);
    if (q.notes) p.set('notes', '1');
    if (q.page) p.set('page', String(q.page));
    if (q.per) p.set('per', String(q.per));
    const qs = p.toString();
    return api.get<AdminReviewsResp>('/api/admin/reviews' + (qs ? '?' + qs : ''));
  },
  deleteReview: (id: number) => api.delete<{ ok: boolean; deleted: number }>('/api/admin/reviews/' + id),
  settings: () => api.get<AdminSettings>('/api/admin/settings'),
  setCheckoutMode: (mode: CheckoutMode) =>
    api.post<{ ok: boolean; checkout_mode: CheckoutMode }>('/api/admin/settings', { checkout_mode: mode }),
  setPricing: (prices: PriceSet) =>
    api.post<{ ok: boolean; prices: PriceSet }>('/api/admin/settings/pricing', prices),
};

// ════════════════════════════════════════════════════════════════════════════
// SEO & Articles module (owner-only). Typed client for /api/admin/seo/*. The AI
// endpoints are server-stubbed today (deterministic templates) — same contract
// when real Claude/OpenAI keys are connected via Integrations.
// ════════════════════════════════════════════════════════════════════════════
export type SeoStatus = 'Published' | 'Draft' | 'Review' | 'Needs Update' | 'Archived';

export interface SeoBlock {
  type: 'heading' | 'paragraph' | 'cta' | 'comparison' | 'faq' | 'image' | string;
  level?: number;
  text?: string;
  title?: string;
  button?: string;
  href?: string;
  q?: string;
  a?: string;
  url?: string;
  alt?: string;
  rows?: { feature: string; riffloop: string; others: string }[];
  [k: string]: unknown;
}

export interface SeoArticle {
  id: string;
  title: string;
  slug: string;
  keyword: string;
  secondary_keywords: string[];
  status: SeoStatus;
  score: number;
  words: number;
  page_type: string;
  intent: string;
  meta_title: string;
  meta_description: string;
  content: SeoBlock[];
  og_image: string | null;
  gen_mode: string | null;
  impressions: number;
  clicks: number;
  ctr: string | null;
  position: string | null;
  conversions: number;
  // Placement in the riffloop.app nav (set in the editor's Publish panel).
  menu_group: string;
  menu_label: string;
  show_in_menu: boolean;
  created_at: string | null;
  updated_at: string | null;
  published_at: string | null;
}

export interface SeoOverview {
  articles: number;
  new_month: number;
  published: number;
  drafts: number;
  avg_score: number;
  keywords: number;
  keywords_top: number;
  competitors: number;
  opportunities: number;
  opportunities_high: number;
}

export interface SeoKeyword {
  id: number;
  term: string;
  volume: string;
  difficulty: string;
  intent: string;
  status: string;
  page: string;
  opportunity: number;
  priority: number; // composite importance (volume + difficulty + intent + opportunity)
  cluster: string;
  created_at: string;
}

export interface SeoCluster {
  cluster: string;
  c: number;
}

export interface SeoCompetitor {
  id: number;
  name: string;
  initial: string;
  category: string;
  priority: string;
  keywords: number;
  notes: string;
  domain: string;
  logo_bg: string;
  created_at: string;
}

export interface SeoLink {
  id: number;
  from_page: string;
  to_page: string;
  anchor: string;
  status: string;
}

export interface SeoLinksResp {
  ok: boolean;
  links: SeoLink[];
  pages: number;
  total: number;
  avg: number;
  orphans: string[];
}

export interface SeoMedia {
  id: number;
  name: string;
  type: string;
  url: string;
  size: number;
  bg: string | null;
  created_at: string;
}

export interface SeoProviderStatus {
  connected: boolean;
  model: string;
  last_tested: string;
  key_masked: string;
}

export interface SeoSettings {
  title_template: string;
  og_image: string;
  canonical: string;
  product_context: string;
  provider: string;
  auto_sitemap: boolean;
  auto_schema: boolean;
  auto_meta: boolean;
  auto_index: boolean;
  openai: SeoProviderStatus;
  claude: SeoProviderStatus;
}

// Result of a generate() call. `kind` echoes the request; the payload fields
// depend on the kind (outline / faqs / meta / audit / gap / article …).
export interface SeoGenResult {
  ok: boolean;
  stub: boolean;
  kind: string;
  note: string;
  content?: SeoBlock[];
  words?: number;
  outline?: { level: string; text: string; tag?: string }[];
  faqs?: { q: string; a: string }[];
  meta_title?: string;
  meta_description?: string;
  titles?: string[];
  links?: { to: string; anchor: string }[];
  covered?: string[];
  missing?: string[];
  missing_topics?: string[];
  missing_faqs?: string[];
  strengths?: string[];
  score?: number;
  checks?: { label: string; score: number; status: string; issue: string; rec: string }[];
  [k: string]: unknown;
}

export interface GenRequest {
  kind: 'article' | 'outline' | 'faqs' | 'meta' | 'title' | 'links' | 'gap' | 'competitors' | 'audit' | 'apply_fix';
  keyword?: string;
  mode?: 'openai' | 'claude' | 'multi';
  page_type?: string;
  intent?: string;
  article_id?: string;
  content?: SeoBlock[];
  fix?: { label: string; issue: string; rec: string };
  fixes?: { label: string; issue: string; rec: string }[];
  meta_title?: string;
  meta_description?: string;
  slug?: string;
}

export const seoApi = {
  overview: () => api.get<SeoOverview & { ok: boolean }>('/api/admin/seo/overview'),

  // Articles
  articles: (q: { q?: string; status?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set('q', q.q);
    if (q.status && q.status.toLowerCase() !== 'all') p.set('status', q.status);
    const qs = p.toString();
    return api.get<{ ok: boolean; articles: SeoArticle[] }>('/api/admin/seo/articles' + (qs ? '?' + qs : ''));
  },
  article: (id: string) => api.get<{ ok: boolean; article: SeoArticle }>('/api/admin/seo/articles/' + encodeURIComponent(id)),
  createArticle: (body: Partial<SeoArticle>) => api.post<{ ok: boolean; article: SeoArticle }>('/api/admin/seo/articles', body),
  updateArticle: (id: string, body: Partial<SeoArticle>) =>
    api.post<{ ok: boolean; article: SeoArticle }>('/api/admin/seo/articles/' + encodeURIComponent(id), body),
  deleteArticle: (id: string) => api.delete<{ ok: boolean }>('/api/admin/seo/articles/' + encodeURIComponent(id)),
  publishArticle: (id: string) =>
    api.post<{ ok: boolean; article: SeoArticle; published_url: string; note: string }>(
      '/api/admin/seo/articles/' + encodeURIComponent(id) + '/publish'),

  // Keywords
  keywords: (q: { cluster?: string; status?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.cluster) p.set('cluster', q.cluster);
    if (q.status) p.set('status', q.status);
    const qs = p.toString();
    return api.get<{ ok: boolean; keywords: SeoKeyword[]; clusters: SeoCluster[] }>(
      '/api/admin/seo/keywords' + (qs ? '?' + qs : ''));
  },
  createKeyword: (body: Partial<SeoKeyword>) => api.post<{ ok: boolean; id: number }>('/api/admin/seo/keywords', body),
  deleteKeyword: (id: number) => api.delete<{ ok: boolean }>('/api/admin/seo/keywords/' + id),
  researchKeywords: (keyword?: string) =>
    api.post<{ ok: boolean; added: string[]; count: number; stub: boolean; keywords: SeoKeyword[]; clusters: SeoCluster[] }>(
      '/api/admin/seo/keywords/research', keyword ? { keyword } : {}),

  // Competitors
  competitors: () => api.get<{ ok: boolean; competitors: SeoCompetitor[] }>('/api/admin/seo/competitors'),
  createCompetitor: (body: Partial<SeoCompetitor>) => api.post<{ ok: boolean; id: number }>('/api/admin/seo/competitors', body),
  deleteCompetitor: (id: number) => api.delete<{ ok: boolean }>('/api/admin/seo/competitors/' + id),

  // Internal links
  links: () => api.get<SeoLinksResp>('/api/admin/seo/links'),

  // Media
  media: () => api.get<{ ok: boolean; media: SeoMedia[] }>('/api/admin/seo/media'),
  uploadMedia: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.postForm<{ ok: boolean; id: number; url: string; type: string; name: string }>('/api/admin/seo/media', fd);
  },
  deleteMedia: (id: number) => api.delete<{ ok: boolean }>('/api/admin/seo/media/' + id),

  // Settings + integrations
  settings: () => api.get<SeoSettings & { ok: boolean }>('/api/admin/seo/settings'),
  saveSettings: (patch: Record<string, unknown>) => api.post<SeoSettings & { ok: boolean }>('/api/admin/seo/settings', patch),
  saveIntegration: (provider: 'openai' | 'claude', body: { action?: 'save' | 'test' | 'disconnect'; key?: string; model?: string }) =>
    api.post<SeoSettings & { ok: boolean }>('/api/admin/seo/integrations/' + provider, body),

  // AI generation (stubbed server-side today)
  generate: (body: GenRequest) => api.post<SeoGenResult>('/api/admin/seo/generate', body),
};


